import {IEvent} from "@webng-types/write-model/index";
import React from "react";
import {DateTimeView, NiceDateTime} from "../NiceDateTime";
import {getSportTiming} from "../utils/eventUtils";
import {useSportEventSpec} from "../utils/useSportEventSpec";
import {useNiceDateTime} from "@webng/react-app-common";
import {useDateTimeDisplay} from "../LiveblogRenderContext";

interface EventDateTimeProps {
  event: IEvent
  children?: React.ReactNode
}


export function EventDateTime({event, children}: EventDateTimeProps) {
  const rawSportTiming = getSportTiming(event)
  const {sportstypeEventSpec} = useSportEventSpec(event.sport_event?.event_type)
  const dateTimeDisplay = useDateTimeDisplay()
  const {dateTime} = useNiceDateTime(event.created_at, true);

  // A synced sport event renders its title next to the icon (SportEventContentView) and its phase
  // here in the meta line, so a mapper that names a status event after its own phase — heim's
  // Halbzeitpause, Ende der regulären Spielzeit, Elfmeterschießen — would state the moment twice.
  // Only an exact match is a duplicate: a phase that says something the title doesn't (Anpfiff in
  // 1. Halbzeit) still belongs here. Scoped to this component deliberately: the horizontal timeline
  // and the teaser badges show the phase WITHOUT the title, so suppressing it there would leave
  // them blank.
  // Gated on the spec being resolved for the same reason as SportEventContentBlockView: the title
  // this would be deduplicating against is only rendered once the asynchronously loaded spec exists,
  // and never for an unknown or removed event type.
  const phaseDuplicatesTitle = !!sportstypeEventSpec
    && !!rawSportTiming?.phase?.name
    && rawSportTiming.phase.name === event.sport_event?.title
  const sportTiming = !phaseDuplicatesTitle ? rawSportTiming
    : rawSportTiming?.timeOfPlay ? {...rawSportTiming, phase: undefined}
    // nothing left to show — fall through to the regular timestamp rather than an empty slot
    : undefined

  // A present display_time override (defined, including "") replaces both the
  // computed date/time and any sport timing — it is the only thing shown, in
  // every dateTimeDisplay mode. Sport timing is likewise unaffected by the mode.
  const hasDisplayOverride = event.display_time != null

  return <React.Fragment>
    {sportTiming && !hasDisplayOverride && <DateTimeView date={(sportTiming.timeOfPlay ? sportTiming.phase?.name : undefined) ?? ''} time={(sportTiming.timeOfPlay || sportTiming.phase?.name) ?? ''} classNamesPrefix="tik4-meta__" title={dateTime}>
      {children}
    </DateTimeView>}
    {(!sportTiming || hasDisplayOverride) && <NiceDateTime ts={event.created_at || "now"} displayOverride={event.display_time} mode={dateTimeDisplay} classNamesPrefix="tik4-meta__">
      {children}
    </NiceDateTime>}
  </React.Fragment>
}
